import { createClient, LiveTranscriptionEvents } from "@deepgram/sdk"

export interface DeepgramSpeechRecognitionOptions {
  lang?: string
  continuous?: boolean
  interimResults?: boolean
  onResult?: (transcript: string, isFinal: boolean) => void
  onError?: (error: string) => void
  onEnd?: () => void
  onSpeechEnd?: () => void
}

export class DeepgramSpeechRecognition {
  private deepgram: any
  private connection: any
  private stream: MediaStream | null = null
  private isRecording = false
  private options: DeepgramSpeechRecognitionOptions
  private audioContext: AudioContext | null = null
  private workletNode: AudioWorkletNode | null = null
  private microphone: MediaStreamAudioSourceNode | null = null

  constructor(options: DeepgramSpeechRecognitionOptions = {}) {
    this.options = options

    if (!import.meta.env.VITE_DEEPGRAM_API_KEY) {
      throw new Error("VITE_DEEPGRAM_API_KEY is not set")
    }

    this.deepgram = createClient(import.meta.env.VITE_DEEPGRAM_API_KEY)
  }

  async start(): Promise<void> {
    if (this.isRecording) {
      return
    }

    try {
      // Get microphone access
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      })

      // Create live transcription connection
      this.connection = this.deepgram.listen.live({
        model: "nova-2",
        language: this.options.lang || "ro",
        smart_format: true,
        interim_results: this.options.interimResults ?? true,
        endpointing: 500,
        encoding: "linear16",
        sample_rate: 16000,
        channels: 1,
      })

      // Set up event listeners
      this.connection.on(LiveTranscriptionEvents.Open, () => {
        console.log("Deepgram connection opened")
        this.startAudioProcessing()
      })

      this.connection.on(LiveTranscriptionEvents.Transcript, (data: any) => {
        console.log("Deepgram transcript data:", data)
        const transcript = data.channel?.alternatives?.[0]?.transcript
        if (transcript && transcript.trim() && this.options.onResult) {
          this.options.onResult(transcript, data.is_final)
        }

        // When endpointing detects end of speech, notify the client.
        if (data.speech_final && this.options.onSpeechEnd) {
          console.log("Endpointing detected end of speech.")
          this.options.onSpeechEnd()
        }
      })

      this.connection.on(LiveTranscriptionEvents.Error, (error: any) => {
        console.error("Deepgram error:", error)
        if (this.options.onError) {
          this.options.onError(error.toString())
        }
      })

      this.connection.on(LiveTranscriptionEvents.Close, () => {
        console.log("Deepgram connection closed")
        if (this.options.onEnd) {
          this.options.onEnd()
        }
      })

      this.isRecording = true
    } catch (error) {
      console.error("Error starting Deepgram recognition:", error)
      if (this.options.onError) {
        this.options.onError(
          error instanceof Error ? error.message : "Unknown error",
        )
      }
    }
  }

  private async startAudioProcessing(): Promise<void> {
    if (!this.stream) return

    try {
      // Create audio context with 16kHz sample rate if possible
      try {
        this.audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)({
          sampleRate: 16000,
        })
      } catch {
        // Fallback to default sample rate
        this.audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)()
      }

      // Load the audio worklet
      await this.audioContext.audioWorklet.addModule("/audio-processor.js")

      // Create source from microphone
      this.microphone = this.audioContext.createMediaStreamSource(this.stream)

      // Create AudioWorkletNode
      this.workletNode = new AudioWorkletNode(
        this.audioContext,
        "audio-processor",
      )

      // Handle messages from the audio worklet
      this.workletNode.port.onmessage = (event) => {
        if (
          event.data.type === "audioData" &&
          this.connection &&
          this.isRecording
        ) {
          const inputData = event.data.data

          // Resample to 16kHz if needed
          const resampledData = this.resampleTo16kHz(
            inputData,
            this.audioContext!.sampleRate,
          )

          // Convert Float32Array to Int16Array (16-bit PCM)
          const pcmData = this.float32ToInt16(resampledData)

          // Send PCM data to Deepgram
          if (pcmData.length > 0) {
            this.connection.send(pcmData.buffer)
          }
        }
      }

      // Connect audio nodes
      this.microphone.connect(this.workletNode)
      this.workletNode.connect(this.audioContext.destination)
    } catch (error) {
      console.error("Error setting up audio worklet:", error)
      // Fallback to ScriptProcessorNode if AudioWorklet is not supported
      this.fallbackToScriptProcessor()
    }
  }

  private fallbackToScriptProcessor(): void {
    if (!this.stream || !this.audioContext) return

    console.warn(
      "AudioWorklet not supported, falling back to ScriptProcessorNode",
    )

    // Create source from microphone
    this.microphone = this.audioContext.createMediaStreamSource(this.stream)

    // Create script processor as fallback
    const processor = this.audioContext.createScriptProcessor(4096, 1, 1)

    // Process audio data
    processor.onaudioprocess = (event) => {
      if (!this.connection || !this.isRecording) return

      const inputBuffer = event.inputBuffer
      const inputData = inputBuffer.getChannelData(0)

      // Resample to 16kHz if needed
      const resampledData = this.resampleTo16kHz(
        inputData,
        this.audioContext!.sampleRate,
      )

      // Convert Float32Array to Int16Array (16-bit PCM)
      const pcmData = this.float32ToInt16(resampledData)

      // Send PCM data to Deepgram
      if (pcmData.length > 0) {
        this.connection.send(pcmData.buffer)
      }
    }

    // Connect audio nodes
    this.microphone.connect(processor)
    processor.connect(this.audioContext.destination)

    // Store reference for cleanup (reusing workletNode property)
    this.workletNode = processor as any
  }

  private resampleTo16kHz(
    inputData: Float32Array,
    inputSampleRate: number,
  ): Float32Array {
    // If already at 16kHz, return as-is
    if (inputSampleRate === 16000) {
      return inputData
    }

    // Simple linear interpolation resampling
    const ratio = inputSampleRate / 16000
    const outputLength = Math.floor(inputData.length / ratio)
    const output = new Float32Array(outputLength)

    for (let i = 0; i < outputLength; i++) {
      const index = i * ratio
      const lower = Math.floor(index)
      const upper = Math.min(Math.ceil(index), inputData.length - 1)
      const fraction = index - lower

      output[i] =
        inputData[lower] * (1 - fraction) + inputData[upper] * fraction
    }

    return output
  }

  private float32ToInt16(buffer: Float32Array): Int16Array {
    const length = buffer.length
    const result = new Int16Array(length)

    for (let i = 0; i < length; i++) {
      const s = Math.max(-1, Math.min(1, buffer[i]))
      result[i] = s < 0 ? s * 0x8000 : s * 0x7fff
    }

    return result
  }

  stop(): void {
    if (!this.isRecording) {
      return
    }

    this.isRecording = false

    // Clean up audio processing
    if (this.workletNode) {
      this.workletNode.disconnect()
      this.workletNode = null
    }

    if (this.microphone) {
      this.microphone.disconnect()
      this.microphone = null
    }

    if (this.audioContext && this.audioContext.state !== "closed") {
      this.audioContext.close()
      this.audioContext = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    if (this.connection) {
      this.connection.finish()
      this.connection = null
    }
  }

  abort(): void {
    this.stop()
  }

  get continuous(): boolean {
    return this.options.continuous ?? false
  }

  set continuous(value: boolean) {
    this.options.continuous = value
  }

  get interimResults(): boolean {
    return this.options.interimResults ?? true
  }

  set interimResults(value: boolean) {
    this.options.interimResults = value
  }

  get lang(): string {
    return this.options.lang || "ro"
  }

  set lang(value: string) {
    this.options.lang = value
  }
}
